import { motion } from "framer-motion";
import { MapPin, Phone, EnvelopeSimple, WhatsappLogo } from "@phosphor-icons/react";
import { CONTACT } from "@/lib/api";

export default function Contact({ onBook }) {
  return (
    <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="rounded-[2.5rem] bg-[var(--navy)] text-white p-8 md:p-16 relative overflow-hidden grain">
          <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-[var(--gold)]/15 blur-3xl" />
          <div className="grid lg:grid-cols-2 gap-12 relative">
            <div>
              <p className="label-caps text-[var(--gold)] mb-4">Get In Touch</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
                Ready when you are.
              </h2>
              <p className="mt-5 text-white/70 max-w-md leading-relaxed">
                Call us, message on WhatsApp, or book online. Quick service,
                spotless results &mdash; across Pune.
              </p>
              <div className="mt-9 space-y-5">
                <a href={`tel:+${CONTACT.phoneIntl}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-[var(--gold)] text-[var(--navy)]">
                    <Phone weight="fill" size={20} />
                  </span>
                  <span className="text-lg font-bold group-hover:text-[var(--gold)] transition-colors">
                    +91 {CONTACT.phone}
                  </span>
                </a>
                <a href={`tel:+${CONTACT.phoneAltIntl}`} className="flex items-center gap-4 group -mt-2" data-testid="contact-phone-alt">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-white/10 text-white">
                    <Phone weight="fill" size={20} />
                  </span>
                  <span className="text-lg font-bold text-white/90 group-hover:text-[var(--gold)] transition-colors">
                    +91 {CONTACT.phoneAlt}
                  </span>
                </a>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-white/10 text-white">
                    <EnvelopeSimple weight="fill" size={20} />
                  </span>
                  <span className="text-white/85 group-hover:text-[var(--gold)] transition-colors break-all">
                    {CONTACT.email}
                  </span>
                </a>
                <div className="flex items-start gap-4">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-white/10 text-white shrink-0">
                    <MapPin weight="fill" size={20} />
                  </span>
                  <span className="text-white/85 leading-relaxed">{CONTACT.address}</span>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-sm p-8 flex flex-col justify-center"
            >
              <h3 className="font-display text-2xl mb-2">Book in seconds</h3>
              <p className="text-white/65 text-sm mb-6">
                Tell us what you need and we'll match you with the right
                verified professional.
              </p>
              <button
                onClick={onBook}
                className="rounded-full bg-[var(--gold)] text-[var(--navy)] font-bold py-4 hover:bg-[var(--gold-soft)] transition-colors mb-3"
                data-testid="contact-book-now"
              >
                Book a Service
              </button>
              <a
                href={`https://wa.me/${CONTACT.phoneIntl}?text=${encodeURIComponent(
                  "Hi Quick Maid Services, I'd like to book a service."
                )}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-bold py-4 hover:bg-white/10 transition-colors"
                data-testid="contact-whatsapp"
              >
                <WhatsappLogo weight="fill" size={20} />
                Chat on WhatsApp
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-[var(--navy-deep)] text-white/70 py-10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center bg-white rounded-xl px-3 py-2">
            <img src="/logo.png" alt="Quick Maid Services Pune" className="h-8 w-auto" />
          </span>
        </div>
        <p className="text-sm">
          &copy; {new Date().getFullYear()} Quick Maid Services. We Care Like Family.
        </p>
        <div className="flex items-center gap-4">
          <a href="/terms" className="text-xs text-white/60 hover:text-[var(--gold)] transition-colors" data-testid="footer-terms-link">
            Terms &amp; Conditions
          </a>
          <a href="/admin/login" className="text-xs text-white/40 hover:text-[var(--gold)] transition-colors" data-testid="footer-admin-link">
            Admin
          </a>
        </div>
      </div>
    </footer>
  );
}
